import { ExclamationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import Button, { ButtonColorTypes } from "../../shared-components/Button";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import { Modal } from "../../shared-components/Modal";
import {
  useDeleteBehaviorKit,
  useVerifyBehaviorKitDelete,
} from "../../api/CompaniesApi";

type Props = {
  companyUUID: string;
  behaviorKitUUID: string;
  onRemove: (behaviorKitUUID: string) => void;
};

const CompanyBehaviorKitRemove = ({
  behaviorKitUUID,
  companyUUID,
  onRemove,
}: Props) => {
  const [showModal, setShowModal] = useState(false);
  const {
    mutate: deleteBehaviorKit,
    isError: errorDeleteBehaviorKit,
    isSuccess: isSucessDelete,
  } = useDeleteBehaviorKit();
  const {
    mutate: verify,
    data: verifyData,
    isError: verifyError,
    isLoading: verifyLoading,
  } = useVerifyBehaviorKitDelete();

  useEffect(() => {
    if (isSucessDelete) {
      handleClose();
      onRemove(behaviorKitUUID);
    }
  }, [behaviorKitUUID, isSucessDelete, onRemove]);

  function handleClose() {
    setShowModal(false);
  }

  function handleRemove() {
    deleteBehaviorKit(behaviorKitUUID);
  }

  return (
    <div>
      <TrashIcon
        className="w-4 cursor-pointer"
        onClick={() => {
          if (verifyData === undefined || verifyError) {
            verify(behaviorKitUUID);
          }
          setShowModal(true);
        }}
      />
      <Modal open={showModal} onClose={handleClose} width={"w-104 p-3"}>
        <div>
          <div className="flex gap-4 mb-4">
            <div className="flex justify-center w-9 h-9 rounded-full bg-red-100">
              <ExclamationCircleIcon className="w-6 text-red-500" />
            </div>
            {(verifyData && verifyData.length > 0) || verifyError ? (
              <div className="flex flex-col">
                <span className="text-sm text-gray-400">
                  This Behavior Kit cannot be removed because it is being used
                  in an active recurring check subscription(s).
                </span>
                <span className="text-sm text-gray-400">
                  Please cancel any active subscription and try again.
                </span>
              </div>
            ) : (
              <div className="flex flex-col">
                <span className="text-lg font-bold mb-2">
                  Remove Behavior Kit?
                </span>
                <span className="text-sm text-gray-400">
                  Removing behavior kit will detach behavior kit from this
                  company.
                </span>
              </div>
            )}
          </div>
          {errorDeleteBehaviorKit && (
            <ErrorMessageContainer message="Couldn't delete behavior kit" />
          )}
          <div className="flex gap-4 justify-end">
            <Button
              text={"Cancel"}
              colorType={ButtonColorTypes.cancel}
              onClick={handleClose}
            />
            <Button
              text="Ok"
              colorType={ButtonColorTypes.secondary}
              onClick={handleRemove}
              disabled={
                verifyLoading || (verifyData?.length ?? 1) > 0 || verifyError
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CompanyBehaviorKitRemove;
